import { useCallback, useEffect, useState } from "react";

type KeySelector = string | string[] | (() => string | string[]);

interface UseLocalStorageOptions<T> {
    prefix?: string;
    expiresIn?: number; // ms
    key: KeySelector;
    initialValue: T | (() => T);
}

interface StoredValue<T> {
    value: T;
    expiresAt?: number;
}

const getKey = (keySelector: KeySelector, prefix?: string): string => {
    const rawKey = typeof keySelector === "function" ? keySelector() : keySelector;

    const keyStr = Array.isArray(rawKey) ? rawKey.join(":") : rawKey;
    return prefix ? `${prefix}:${keyStr}` : keyStr;
};

// Todo: Remove this function when cookie solution is available
export function useLocalStorage<T>({
    key,
    prefix,
    expiresIn,
    initialValue,
}: UseLocalStorageOptions<T>): [T, (value: T) => void, () => void] {
    const storageKey = getKey(key, prefix);

    const getStoredValue = useCallback((): T => {
        try {
            const item = localStorage.getItem(storageKey);
            if (!item) return typeof initialValue === "function" ? (initialValue as () => T)() : initialValue;

            const parsed: StoredValue<T> = JSON.parse(item);
            if (parsed.expiresAt && parsed.expiresAt < Date.now()) {
                localStorage.removeItem(storageKey);
                return typeof initialValue === "function" ? (initialValue as () => T)() : initialValue;
            }

            return parsed.value;
        } catch (error) {
            console.warn("Failed to read from localStorage:", error);
            return typeof initialValue === "function" ? (initialValue as () => T)() : initialValue;
        }
    }, [storageKey, initialValue]);

    const [value, setValue] = useState<T>(getStoredValue);

    const setStoredValue = (newValue: T) => {
        try {
            const stored: StoredValue<T> = {
                value: newValue,
                expiresAt: expiresIn ? Date.now() + expiresIn : undefined,
            };
            localStorage.setItem(storageKey, JSON.stringify(stored));
            setValue(newValue);
        } catch (error) {
            console.warn("Failed to write to localStorage:", error);
        }
    };

    const removeStoredValue = () => {
        localStorage.removeItem(storageKey);
        setValue(typeof initialValue === "function" ? (initialValue as () => T)() : initialValue);
    };

    useEffect(() => {
        const handleStorage = (e: StorageEvent) => {
            if (e.key === storageKey) {
                setValue(getStoredValue());
            }
        };
        window.addEventListener("storage", handleStorage);
        return () => window.removeEventListener("storage", handleStorage);
    }, [storageKey, getStoredValue]);

    return [value, setStoredValue, removeStoredValue];
}
